import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from './cn';

/**
 * The remaining form controls, sharing the height, border, radius and focus ring
 * defined by `Input`. Anything that takes user input in the app should come from
 * here so that a form reads as one thing rather than a stack of one-offs.
 */

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  hint?: string;
  error?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, className, id, children, ...props },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={fieldId} className="mb-1.5 block font-sans text-caption font-medium text-text-primary">
          {label}
        </label>
      )}
      <div className="relative">
        <select
          ref={ref}
          id={fieldId}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined}
          className={cn(
            'h-11 w-full appearance-none rounded-md border border-input-border bg-input pl-3.5 pr-10',
            'text-[0.9375rem] text-text-primary transition-colors duration-fast',
            'hover:border-muted-foreground/40 focus:border-primary focus:shadow-focus focus:outline-none',
            'disabled:cursor-not-allowed disabled:opacity-50',
            error && 'border-error focus:border-error',
            className,
          )}
          {...props}
        >
          {children}
        </select>
        <ChevronDown
          size={16}
          aria-hidden="true"
          className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
      </div>
      {error ? (
        <p id={`${fieldId}-error`} role="alert" className="mt-1.5 text-caption text-error">
          {error}
        </p>
      ) : hint ? (
        <p id={`${fieldId}-hint`} className="mt-1.5 text-caption text-text-secondary">
          {hint}
        </p>
      ) : null}
    </div>
  );
});

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  hint?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, hint, className, id, ...props },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;

  return (
    <div className={cn('flex gap-3', className)}>
      {/* The native input stays in the layout (not display:none) so it keeps its
          hit area and the focus ring can be drawn on the styled box via peer-. */}
      <span className="relative mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center">
        <input
          ref={ref}
          id={fieldId}
          type="checkbox"
          className="peer absolute inset-0 h-full w-full cursor-pointer appearance-none rounded-sm border border-input-border bg-input transition-colors checked:border-primary checked:bg-primary focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50"
          {...props}
        />
        <Check
          size={13}
          strokeWidth={3}
          aria-hidden="true"
          className="pointer-events-none relative hidden text-white peer-checked:block"
        />
      </span>
      <span className="min-w-0">
        <label htmlFor={fieldId} className="cursor-pointer text-[0.9375rem] text-text-primary">
          {label}
        </label>
        {hint && <p className="mt-0.5 text-caption text-text-secondary">{hint}</p>}
      </span>
    </div>
  );
});

export interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  hint?: string;
}

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch(
  { label, hint, className, id, ...props },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;

  return (
    <label htmlFor={fieldId} className={cn('flex cursor-pointer items-center justify-between gap-4', className)}>
      <span className="min-w-0">
        <span className="block text-[0.9375rem] font-medium text-text-primary">{label}</span>
        {hint && <span className="mt-0.5 block text-caption text-text-secondary">{hint}</span>}
      </span>
      <span className="relative inline-flex shrink-0">
        <input ref={ref} id={fieldId} type="checkbox" className="peer sr-only" {...props} />
        <span className="h-6 w-11 rounded-full bg-input-border transition-colors duration-fast peer-checked:bg-primary peer-focus-visible:shadow-focus peer-disabled:opacity-50" />
        <span className="pointer-events-none absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-xs transition-transform duration-fast peer-checked:translate-x-5" />
      </span>
    </label>
  );
});

export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  hint?: string;
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { label, hint, className, id, ...props },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;

  return (
    <div className={cn('flex gap-3', className)}>
      <span className="relative mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center">
        <input
          ref={ref}
          id={fieldId}
          type="radio"
          className="peer absolute inset-0 h-full w-full cursor-pointer appearance-none rounded-full border border-input-border bg-input transition-colors checked:border-primary focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50"
          {...props}
        />
        <span className="pointer-events-none relative hidden h-2 w-2 rounded-full bg-primary peer-checked:block" />
      </span>
      <span className="min-w-0">
        <label htmlFor={fieldId} className="cursor-pointer text-[0.9375rem] text-text-primary">
          {label}
        </label>
        {hint && <p className="mt-0.5 text-caption text-text-secondary">{hint}</p>}
      </span>
    </div>
  );
});
